// src/settings/GroupConditionModal.js
import React, { useState } from "react";

const conditionTypeLabels = {
  dept: "診療科条件",
  procedure: "術式条件",
  experience: "経験年数条件",
  schoolYear: "学年条件",
  manual: "個人指定条件"
};

function GroupConditionModal({ groupName, conditionGroups, onClose, onEdit }) {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0,0,0,0.7)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
    modalContainer: {
      backgroundColor: "white",
      borderRadius: "10px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
      width: "90%",
      maxWidth: "800px",
      maxHeight: "85vh",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    header: {
      borderBottom: "1px solid #eee",
      padding: "1.25rem 1.5rem",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    title: {
      margin: 0,
      fontSize: "1.3rem",
      fontWeight: "600",
      color: "#333",
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      color: "#666",
      cursor: "pointer",
    },
    body: {
      padding: "1.5rem",
      overflowY: "auto",
      flex: 1,
    },
    conditionGroup: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      padding: "1.5rem",
      marginBottom: "1rem",
    },
    groupTitle: {
      fontSize: "1.1rem",
      fontWeight: "600",
      color: "#334155",
      marginBottom: "1rem",
    },
    minimumMatch: {
      backgroundColor: "#e0f2fe",
      padding: "0.75rem",
      borderRadius: "6px",
      marginBottom: "1rem",
      fontSize: "0.95rem",
      color: "#0369a1",
    },
    conditionItem: {
      backgroundColor: "white",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      padding: "1rem",
      marginBottom: "0.75rem",
    },
    conditionHeader: {
      fontSize: "0.95rem",
      fontWeight: "600",
      color: "#475569",
      marginBottom: "0.5rem",
    },
    conditionDetail: {
      fontSize: "0.9rem",
      color: "#64748b",
      marginBottom: "0.25rem",
    },
    emptyMessage: {
      textAlign: "center",
      color: "#666",
      padding: "2rem",
      fontSize: "0.95rem",
    },
    footer: {
      borderTop: "1px solid #eee",
      padding: "1rem 1.5rem",
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: "#f9f9f9",
    },
    closeButtonFooter: {
      backgroundColor: "#6c757d",
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "0.6rem 1.5rem",
      cursor: "pointer",
      fontSize: "0.95rem",
      fontWeight: "500",
    },
    badge: {
      display: "inline-block",
      padding: "0.25rem 0.5rem",
      borderRadius: "20px",
      fontSize: "0.85rem",
      fontWeight: "500",
      color: "white",
      backgroundColor: "#3b82f6",
      marginLeft: "0.5rem",
    },
  };

  const renderConditionDetails = (condition) => {
    switch (condition.type) {
      case "schoolYear":
        return (
          <div style={styles.conditionDetail}>
            対象学年: {condition.targetSchoolYear}年生
          </div>
        );
      
      case "experience":
        return (
          <div style={styles.conditionDetail}>
            経験年数: {condition.minExperience || 0}年以上 〜 {condition.maxExperience || 99}年以下
          </div>
        );
      
      case "dept":
        return (
          <>
            <div style={styles.conditionDetail}>
              診療科: {condition.department}
            </div>
            {condition.acceptableLevels && condition.acceptableLevels.length > 0 && (
              <div style={styles.conditionDetail}>
                受け入れ可能レベル: {condition.acceptableLevels.join(", ")}
              </div>
            )}
          </>
        );
      
      case "procedure":
        return (
          <>
            <div style={styles.conditionDetail}>
              術式: {condition.procedureName}
            </div>
            <div style={styles.conditionDetail}>
              役割: {condition.role === "both" ? "器械出し・外回り両方" : 
                    condition.role === "scrub" ? "器械出しのみ" : "外回りのみ"}
            </div>
            {condition.acceptableLevels && condition.acceptableLevels.length > 0 && (
              <div style={styles.conditionDetail}>
                受け入れ可能レベル: {condition.acceptableLevels.join(", ")}
              </div>
            )}
          </>
        );
      
      case "manual":
        return (
          <div style={styles.conditionDetail}>
            対象スタッフ: {condition.manualMembers?.length || 0}人
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>
            {groupName} の条件設定
            <span style={styles.badge}>{conditionGroups?.length || 0}個</span>
          </h3>
          <button style={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div style={styles.body}>
          {!conditionGroups || conditionGroups.length === 0 ? (
            <div style={styles.emptyMessage}>
              条件グループが設定されていません
            </div>
          ) : (
            conditionGroups.map((group, groupIdx) => (
              <div key={groupIdx} style={styles.conditionGroup}>
                <div style={styles.groupTitle}>
                  条件グループ {groupIdx + 1}
                </div>
                
                <div style={styles.minimumMatch}>
                  最低一致数: 以下の{group.conditions?.length || 0}個の条件のうち、
                  {group.minimumMatch}個以上を満たすスタッフ
                </div>

                {group.conditions && group.conditions.length > 0 ? (
                  group.conditions.map((condition, condIdx) => (
                    <div key={condIdx} style={styles.conditionItem}>
                      <div style={styles.conditionHeader}>
                        条件 {condIdx + 1}: {conditionTypeLabels[condition.type]}
                      </div>
                      {renderConditionDetails(condition)}
                    </div>
                  ))
                ) : (
                  <div style={styles.emptyMessage}>
                    条件が設定されていません
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div style={styles.footer}>
        <button 
            onClick={onEdit}
            style={{
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "6px",
            padding: "0.6rem 1.5rem",
            cursor: "pointer",
            fontSize: "0.95rem",
            fontWeight: "500",
            }}
        >
            📝 編集
        </button>
        <button onClick={onClose} style={styles.closeButtonFooter}>
            閉じる
        </button>
        </div>
      </div>
    </div>
  );
}

export default GroupConditionModal;